(() => {
  const script = document.querySelector('script[data-ga-cart-context]');
  if (!script) return;

  const cartUrl = script.dataset.cartUrl;
  const cartUpdateUrl = script.dataset.cartUpdateUrl;
  const sessionCookieName = script.dataset.sessionCookie;
  if (!cartUrl || !cartUpdateUrl || !sessionCookieName) return;

  const storageKey = 'lagea:ga-cart-context:v1';
  const syncTtlMs = 30 * 60 * 1000;
  let syncTimer;
  let activeSync;
  let memoryState;

  const getCookie = (name) => {
    const prefix = `${encodeURIComponent(name)}=`;
    const cookie = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));

    if (!cookie) return '';
    try {
      return decodeURIComponent(cookie.slice(prefix.length));
    } catch (_error) {
      return '';
    }
  };

  const parseClientId = (cookie) => {
    const match = cookie.match(/(?:^|\.)(\d+\.\d+)$/);
    return match ? match[1] : '';
  };

  const parseSessionId = (cookie) => {
    const gs2Match = cookie.match(/^GS2\.\d+\.s(\d+)(?:\$|$)/);
    if (gs2Match) return gs2Match[1];

    const gs1Match = cookie.match(/^GS\d+\.\d+\.(\d+)(?:\.|$)/);
    return gs1Match ? gs1Match[1] : '';
  };

  const getAnalyticsConsent = () => {
    const customerPrivacy = window.Shopify?.customerPrivacy;
    if (typeof customerPrivacy?.analyticsProcessingAllowed !== 'function') return null;
    return customerPrivacy.analyticsProcessingAllowed();
  };

  const readState = () => {
    try {
      return JSON.parse(window.localStorage.getItem(storageKey)) || memoryState;
    } catch (_error) {
      return memoryState;
    }
  };

  const writeState = (state) => {
    memoryState = state;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (_error) {
      // Tracking must never interrupt shopping when storage is unavailable.
    }
  };

  const fingerprint = (value) => {
    let hash = 5381;
    for (let index = 0; index < value.length; index += 1) {
      hash = ((hash << 5) + hash) ^ value.charCodeAt(index);
    }
    return (hash >>> 0).toString(36);
  };

  const fetchCart = async () => {
    const response = await fetch(cartUrl, {
      credentials: 'same-origin',
      keepalive: true,
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('cart_read_failed');
    return response.json();
  };

  const updateAttributes = async (attributes) => {
    const response = await fetch(cartUpdateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ attributes })
    });
    if (!response.ok) throw new Error('cart_update_failed');
  };

  let beaconSent = false;

  const beaconUpdateAttributes = (attributes) => {
    if (typeof navigator.sendBeacon !== 'function') return false;
    const blob = new Blob([JSON.stringify({ attributes })], { type: 'application/json' });
    return navigator.sendBeacon(cartUpdateUrl, blob);
  };

  // Dynamic checkout buttons (Shop Pay/PayPal/Google Pay) navigate to checkout
  // without going through /cart/add.js, so they never trigger the cartUpdate
  // pubsub sync below. A beacon on page hide is the only path left to capture
  // the context before the browser tears down the page.
  const flushOnExit = () => {
    if (beaconSent) return;
    if (getAnalyticsConsent() !== true) return;

    const clientId = parseClientId(getCookie('_ga'));
    if (!clientId) return;

    const sessionId = parseSessionId(getCookie(sessionCookieName));
    beaconSent = beaconUpdateAttributes({
      __ga_client_id: clientId,
      __ga_session_id: sessionId,
      __ga_captured_at: new Date().toISOString(),
      __ga_analytics_allowed: 'true'
    });
  };

  const sync = async () => {
    const consent = getAnalyticsConsent();
    if (consent === null) return;

    const previous = readState();
    if (!consent) {
      if (previous?.allowed !== true) return;
      await updateAttributes({
        __ga_client_id: '',
        __ga_session_id: '',
        __ga_captured_at: '',
        __ga_analytics_allowed: 'false'
      });
      writeState({ allowed: false, syncedAt: Date.now() });
      beaconSent = true;
      return;
    }

    const clientId = parseClientId(getCookie('_ga'));
    if (!clientId) return;

    const sessionId = parseSessionId(getCookie(sessionCookieName));
    const cart = await fetchCart();
    const currentFingerprint = fingerprint(`${cart.token || ''}|${clientId}|${sessionId}`);
    if (
      previous?.allowed === true
      && previous.fingerprint === currentFingerprint
      && Date.now() - previous.syncedAt < syncTtlMs
    ) {
      beaconSent = true;
      return;
    }

    await updateAttributes({
      __ga_client_id: clientId,
      __ga_session_id: sessionId,
      __ga_captured_at: new Date().toISOString(),
      __ga_analytics_allowed: 'true'
    });
    writeState({ allowed: true, fingerprint: currentFingerprint, syncedAt: Date.now() });
    beaconSent = true;
  };

  const runSync = () => {
    if (activeSync) return activeSync;
    activeSync = sync()
      .catch(() => undefined)
      .finally(() => {
        activeSync = undefined;
      });
    return activeSync;
  };

  const scheduleSync = (delay = 0) => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(runSync, delay);
  };

  document.addEventListener('visitorConsentCollected', () => scheduleSync());
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleSync(50));
  }

  document.addEventListener('pagehide', flushOnExit);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnExit();
  });

  [0, 1000, 3000, 10000].forEach((delay) => window.setTimeout(runSync, delay));
})();
